import { neutral, Text } from "@flat-ui";
import { Box, Stack, SvgIconProps } from "@mui/material";
import { withEllipsis } from "@styles/common-styles";
import { ListItemMenu } from "@components/common/mobile/list-item-menu";

/** Describes a single menu option for the mobile list item. */
export interface MenuItemOption<T> {
  /** Visible text of the option */
  label: string;
  /** Icon component to render for the option */
  Icon: (props: SvgIconProps) => JSX.Element;
  /** Handler invoked when the option is activated with the item */
  onSelect: (item: T) => void;
  /** Optional callback to get the href url of each menu item */
  getHrefUrl?: (item: T) => string;
}

interface Props<T> {
  /** The item being rendered */
  item: T;
  /** Thumbnail or image URL shown on the left */
  image: string;
  /** Main title text */
  title: string;
  /** Optional URL; when provided, tile renders as a link instead of a clickable div */
  href?: string;
  /** Subtitle text shown above the title */
  subtitle: string;
  /** Secondary text shown at the bottom */
  secondaryText: string;
  /** Optional menu options; when provided a three-dots menu is shown. */
  menuItems?: MenuItemOption<T>[];
}

/**
 * Renders a mobile-friendly list item card with image, title, subtitle, optional secondary
 * text and an optional three-dots menu.
 *
 * @template T - The type of item being rendered
 */
export function ListItemCardMobile<T>({
  item,
  image,
  title,
  href,
  subtitle,
  secondaryText,
  menuItems,
}: Props<T>): JSX.Element {
  const component = href ? "a" : "div";
  const componentProps = href
    ? { href, target: "_self", rel: "noopener noreferrer" }
    : {};

  return (
    <Stack
      component={component}
      flexDirection="row"
      gap={1}
      p={1}
      {...componentProps}
      sx={{
        cursor: href ? "pointer" : "default",
        textDecoration: "none",
        color: "inherit",
      }}
    >
      <Box
        component="img"
        src={image}
        alt={title}
        sx={{ width: 132, height: 96, objectFit: "cover" }}
      />

      <Stack flex={1} minWidth={0} justifyContent="space-between">
        <Stack gap={0.5}>
          <Text variant="bodyS">{subtitle}</Text>
          <Text component="var" variant="heading16" sx={withEllipsis}>
            {title}
          </Text>
        </Stack>
        <Text
          variant="bodyS"
          sx={{ textTransform: "capitalize", color: neutral[700] }}
        >
          {secondaryText}
        </Text>
      </Stack>

      {menuItems && (
        <Stack alignItems="center" justifyContent="center">
          <ListItemMenu<T> item={item} menuItems={menuItems} />
        </Stack>
      )}
    </Stack>
  );
}
